<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useId, watch } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import * as rutHelpers from 'rut-helpers';
import { patientsApi } from '@/api/patients';
import { useAddressGeocoding, type GeocodeResult } from '@/composables/useAddressGeocoding';
import GroupMembershipPicker from '@/components/groups/group-membership-picker.vue';
import { type PatientDraft, type PatientErrors, dniError } from './erp-patient-draft';
import { formatDate } from './erp-format';

// The incorporation screen's first step: who the Sales orders go to and the
// new patient's main address. The go-live date is the screen's default (the
// Sales orders' first day, or today), read back on the summary.

const props = defineProps<{
  // The fields the AI read in the notes, for the coordinator to check.
  fromNotes: string[];
  incorporatedSince: string | null;
  errors: PatientErrors;
  groupPicker: { show: boolean; required: boolean; items: { value: number; title: string }[];
    hint: string; error: string | null };
}>();
const patient = defineModel<PatientDraft>('patient', { required: true });
const groupIds = defineModel<number[]>('groupIds', { required: true });

const { data: patients } = useQuery({
  queryKey: ['patients', 'forFilters'],
  queryFn: () => patientsApi.indexForFilters(),
  enabled: computed(() => patient.value.mode === 'existing'),
  initialData: [],
});
const patientItems = computed(() => patients.value.map((item) => ({
  value: item.id, title: item.dni ? `${item.fullName} (${item.dni})` : item.fullName,
})));

// The RUT is formatted as it is typed and checked once the field is left, as in
// the patient and caregiver forms.
const dniTouched = ref(false);
watch(() => patient.value.dni, (value) => {
  const formatted = rutHelpers.rutFormat(value ?? '');
  if (formatted !== value) patient.value.dni = formatted;
}, { immediate: true });
const dniMessage = computed(() => props.errors.dni ??
  (dniTouched.value && patient.value.dni ? dniError(patient.value.dni) : undefined));

const FIELD_NAMES: Record<string, string> = {
  // eslint-disable-next-line camelcase
  first_name: 'nombre', last_name: 'apellidos', dni: 'RUT',
};
const readByAi = computed(() => {
  const names = props.fromNotes.map((field) => FIELD_NAMES[field]);
  if (!names.length) return '';
  const listed = names.length === 1 ? names[0] :
    `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;

  return `Leído por IA en las notas: ${listed}. Revisa antes de continuar.`;
});

// The main address, picked from Google's suggestions so it has coordinates
// (as in the patient form).
const { attachAutocomplete, openMapPicker } = useAddressGeocoding();
const uid = useId();
const ADDRESS_ID = `incorporation-address-${uid}`;
const MAP_ID = `incorporation-address-map-${uid}`;
const showMap = ref(false);
const located = computed(() => !!(patient.value.latitude && patient.value.longitude));

function applyGeocode(result: GeocodeResult) {
  if (result.address) patient.value.address = result.address;
  if (result.latitude) patient.value.latitude = result.latitude;
  if (result.longitude) patient.value.longitude = result.longitude;
}

// A hand-typed address is no longer where the map put it.
function onAddressInput() {
  patient.value.latitude = null;
  patient.value.longitude = null;
}

let detach: (() => void) | null = null;

// Only a new patient's address field is there to attach to.
async function attach() {
  detach?.();
  detach = null;
  if (patient.value.mode !== 'new') return;

  await nextTick();
  detach = await attachAutocomplete(ADDRESS_ID, applyGeocode);
}

onMounted(attach);
watch(() => patient.value.mode, attach);
onUnmounted(() => detach?.());

async function openMap() {
  if (!located.value) return;
  showMap.value = true;
  await nextTick();
  const position = { lat: parseFloat(patient.value.latitude as string),
    lng: parseFloat(patient.value.longitude as string) };
  await openMapPicker(MAP_ID, position, applyGeocode);
}
</script>
<template>
  <div>
    <v-radio-group
      v-model="patient.mode"
      label="Paciente"
      inline
      hide-details
      class="mb-2"
    >
      <v-radio
        label="Paciente nuevo"
        value="new"
      />
      <v-radio
        label="Paciente que ya está en Kuido"
        value="existing"
      />
    </v-radio-group>
    <v-row
      v-if="patient.mode === 'new'"
      dense
    >
      <v-col
        cols="12"
        sm="4"
      >
        <v-text-field
          v-model="patient.firstName"
          label="Nombre"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :error-messages="errors.firstName"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-text-field
          v-model="patient.lastName"
          label="Apellidos"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :error-messages="errors.lastName"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-text-field
          v-model="patient.dni"
          label="RUT"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :error-messages="dniMessage"
          @blur="dniTouched = true"
        />
      </v-col>
      <v-col
        v-if="readByAi"
        cols="12"
        class="text-caption text-medium-emphasis"
      >
        {{ readByAi }}
      </v-col>
      <v-col cols="12">
        <v-text-field
          :id="ADDRESS_ID"
          v-model="patient.address"
          label="Dirección principal"
          placeholder="Busca y selecciona una dirección…"
          prepend-inner-icon="mdi-magnify"
          autocomplete="off"
          variant="outlined"
          density="compact"
          hide-details="auto"
          :error-messages="errors.address"
          @input="onAddressInput"
        >
          <template #append-inner>
            <v-btn
              icon="mdi-map-search-outline"
              variant="text"
              size="small"
              aria-label="Ajustar dirección en mapa"
              :disabled="!located"
              @click="openMap"
            />
          </template>
        </v-text-field>
        <div
          v-if="showMap"
          class="mt-2"
        >
          <div
            :id="MAP_ID"
            class="address-map"
          />
          <div class="d-flex justify-end mt-1">
            <v-btn
              variant="text"
              size="small"
              @click="showMap = false"
            >
              Listo
            </v-btn>
          </div>
        </div>
      </v-col>
      <v-col
        v-if="groupPicker.show"
        cols="12"
      >
        <group-membership-picker
          v-model="groupIds"
          :required="groupPicker.required"
          :items="groupPicker.items"
          :hint="groupPicker.hint"
          :error-message="groupPicker.error"
        />
      </v-col>
    </v-row>
    <v-autocomplete
      v-else
      v-model="patient.existingId"
      :items="patientItems"
      label="Paciente"
      variant="outlined"
      density="compact"
      hide-details="auto"
      :error-messages="errors.existing"
    />
    <v-alert
      v-if="incorporatedSince"
      type="info"
      variant="tonal"
      density="compact"
      class="mt-4"
    >
      Este paciente ya está incorporado: Kuido envía sus sesiones a Odoo desde el
      {{ formatDate(incorporatedSince) }}.
    </v-alert>
  </div>
</template>
<style scoped>
.address-map {
  width: 100%;
  height: 320px;
}
</style>
